import { isNonNullObject } from 'packages/lib/common-types/types';
import {
  getDefaultHeaders,
  APIBackend,
  getBaseURLForAPIBackend,
} from 'packages/lib/utils/fetch';
import { preparePatchArray } from 'packages/lib/utils/api';
import { AnyObject } from 'packages/lib/common-types/types';
import { logError } from 'packages/main/components/Videos/errorLogger';
import {
  ResponseNotOk,
  UnexpectedResponseType,
} from 'packages/lib/utils/error';

const logErrorResponse = async (resp: Response): Promise<void> => {
  try {
    // try to log API error message
    const errorResponse = await resp?.json?.();
    if (errorResponse) {
      logError(
        `non-200 API Response (code ${resp.status}) for ${
          resp.url
        }: ${JSON.stringify(errorResponse['error'])}`
      );
    }
  } catch (err) {}
};

/** @param useOpPathFormat For Annotations backend: keep useOpPathFormat=true (default), as the Annotations backend API expects data in this format:
 * https://datatracker.ietf.org/doc/html/rfc6902
 * as described in 'Method 3' here:
 * https://medium.com/@isuru89/a-better-way-to-implement-http-patch-operation-in-rest-apis-721396ac82bf
 *
 * Use useOpPathFormat=false for any other backend that takes the record in the same format as a POST, but with only the fields you want modified.
 *
 * returns Promise with results of resp.json() (if data was returned) and full HTTP response. */
export const patch = async <T extends AnyObject>(
  backend: APIBackend,
  endpoint: string,
  patchData: T,
  useOpPathFormat = true
): Promise<[unknown, Response]> => {
  const resp = await fetch(`${getBaseURLForAPIBackend(backend)}${endpoint}`, {
    method: 'PATCH',
    body: JSON.stringify(
      useOpPathFormat ? preparePatchArray(patchData) : patchData
    ),
    headers: {
      ...(backend === 'annotations' && { 'If-Match': '*' }),
      ...(await getDefaultHeaders()).headers,
    },
  });

  if (!resp.ok) {
    void logErrorResponse(resp);
    throw new ResponseNotOk(resp);
  }
  // Some PATCH backends may return a response, many do not. Return response JSON to caller when available.
  let data = null;
  if (resp.status !== 204) {
    try {
      data = await resp.json();
    } catch (e) {}
  }

  return [data, resp];
};

/** returns Promise with results of resp.json() and full HTTP response. */
export function post<T, K = { token: string }>(
  backend: APIBackend,
  endpoint: string,
  postData: T,
  typeTest?: (r: unknown) => r is K,
  parseResponse?: true
): Promise<[K, Response]>;

export function post<T, K = { token: string }>(
  backend: APIBackend,
  endpoint: string,
  postData: T,
  typeTest?: (r: unknown) => r is K,
  parseResponse?: false
): Promise<[undefined, Response]>;

/** returns Promise with results of resp.json() (if requested) and full HTTP response. */
export async function post<T, K = { token: string }>(
  backend: APIBackend,
  endpoint: string,
  postData: T,
  typeTest = (r: unknown): r is K =>
    isNonNullObject(r) && typeof r.token === 'string',
  parseResponse = true
): Promise<[unknown, Response]> {
  const req = new Request(`${getBaseURLForAPIBackend(backend)}${endpoint}`, {
    method: 'POST',
    body: JSON.stringify(postData),
    headers: {
      ...(backend === 'annotations' && { 'If-Match': '*' }),
      ...(await getDefaultHeaders()).headers,
    },
  });

  const resp = await fetch(req);
  if (!resp.ok) {
    void logErrorResponse(resp);
    throw new ResponseNotOk(resp);
  }
  if (!parseResponse) return [undefined, resp];
  const respJson = await resp.json();
  if (!typeTest(respJson)) throw new UnexpectedResponseType(resp);
  return [respJson, resp];
}

/** returns Promise with results of resp.json() (only if there was data returned) and full HTTP response. */
export const remove = async (
  backend: APIBackend,
  endpoint: string
): Promise<[unknown, Response]> => {
  const resp = await fetch(`${getBaseURLForAPIBackend(backend)}${endpoint}`, {
    method: 'DELETE',
    headers: {
      ...(backend === 'annotations' && { 'If-Match': '*' }),
      ...(await getDefaultHeaders()).headers,
    },
  });
  if (!resp.ok) {
    void logErrorResponse(resp);
    throw new ResponseNotOk(resp);
  }
  // Some DELETE backends may return a response, many do not. Return response JSON to caller when available.
  let data = null;
  if (resp.status !== 204)
    try {
      data = await resp.json();
    } catch (e) {}
  return [data, resp];
};
